/** Auth endpoints — thin wrappers around the FastAPI backend. */

import { ApiClient } from "./client";

export type LanguageCode = "en" | "uk";

export type Role = "user" | "admin" | "superadmin";

export interface UserPublic {
  id: string;
  email: string;
  created_at: string;
  language: LanguageCode | null;
  timezone: string;
  display_name: string | null;
  avatar_url: string | null;
  share_code: string;
  /**
   * Owner-only activity tracking. Other users never see these
   * fields — cross-user reads go through the People API, which
   * applies the opt-in gate. ``activity_visible`` controls whether
   * connected friends/family see the timestamps in their People list.
   */
  last_seen_at: string | null;
  last_action_at: string | null;
  activity_visible: boolean;
  /**
   * Operator role — gates the ``/admin/*`` surface. ``superadmin``
   * additionally unlocks role-change mutations. New accounts always
   * start as ``user``; promotion happens through the admin API or
   * the ``BOOTSTRAP_ADMIN_EMAIL`` startup hook.
   */
  role: Role;
  /**
   * Soft-lock timestamp. When non-null the backend refuses every
   * auth surface (login, refresh, ``GET /auth/me``, Telegram). The
   * SPA never sees a deactivated account except via the admin API,
   * so for the in-memory ``user`` this stays ``null`` in practice.
   */
  deactivated_at: string | null;
}

export interface TokenBundle {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
}

export interface AuthResponse {
  user: UserPublic;
  tokens: TokenBundle;
}

export interface Credentials {
  email: string;
  password: string;
}

export const authApi = (client: ApiClient) => ({
  signup(credentials: Credentials): Promise<AuthResponse> {
    return client.request<AuthResponse>("/auth/signup", {
      method: "POST",
      json: credentials,
    });
  },

  login(credentials: Credentials): Promise<AuthResponse> {
    return client.request<AuthResponse>("/auth/login", {
      method: "POST",
      json: credentials,
    });
  },

  logout(refreshToken: string): Promise<void> {
    return client.request<void>("/auth/logout", {
      method: "POST",
      json: { refresh_token: refreshToken },
    });
  },

  requestPasswordReset(email: string): Promise<{ status: "accepted" }> {
    return client.request<{ status: "accepted" }>(
      "/auth/password-reset/request",
      { method: "POST", json: { email } },
    );
  },

  confirmPasswordReset(token: string, newPassword: string): Promise<{ status: "accepted" }> {
    return client.request<{ status: "accepted" }>(
      "/auth/password-reset/confirm",
      { method: "POST", json: { token, new_password: newPassword } },
    );
  },

  /**
   * Mints a single-use Telegram link code. The user pastes
   * ``/start <code>`` into the bot to attach their chat.
   */
  issueTelegramLinkCode(): Promise<{ code: string; expires_at: string }> {
    return client.request<{ code: string; expires_at: string }>(
      "/auth/telegram/link-code",
      { method: "POST" },
    );
  },

  /** Returns the authenticated user's profile, including ``language``. */
  me(): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me", { method: "GET" });
  },

  /**
   * Sets the user's explicit language pick (or ``null`` to clear).
   * The backend uses this for the Telegram bot's reply language;
   * the SPA also reads it on login to seed the in-memory choice.
   */
  setLanguage(language: LanguageCode | null): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/language", {
      method: "PATCH",
      json: { language },
    });
  },

  /**
   * Sets the user's IANA timezone (e.g. ``Europe/Kyiv``). Used by the
   * per-habit reminder beat task to fire DMs at the user's local time.
   */
  setTimezone(timezone: string): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/timezone", {
      method: "PATCH",
      json: { timezone },
    });
  },

  setProfile(payload: { display_name?: string | null }): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/profile", {
      method: "PATCH",
      json: payload,
    });
  },

  uploadAvatar(file: File): Promise<UserPublic> {
    const form = new FormData();
    form.append("file", file);
    return client.request<UserPublic>("/auth/me/avatar", {
      method: "POST",
      body: form,
    });
  },

  deleteAvatar(): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/avatar", { method: "DELETE" });
  },

  rotateShareCode(): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/share-code/rotate", {
      method: "POST",
    });
  },

  /**
   * Flip the opt-in flag that exposes ``last_seen_at`` /
   * ``last_action_at`` to accepted friends/family. Returns the
   * refreshed user.
   */
  setActivityVisibility(visible: boolean): Promise<UserPublic> {
    return client.request<UserPublic>("/auth/me/activity-visibility", {
      method: "PATCH",
      json: { visible },
    });
  },
});
